import React, { useState, useEffect } from 'react';
import { motion, AnimatePresence } from 'framer-motion';

const OmIcon = () => (
  <svg width="32" height="32" viewBox="0 0 100 100" fill="none" xmlns="http://www.w3.org/2000/svg">
    <path d="M20 65 Q15 45 30 35 Q45 25 55 35 Q65 45 55 55 Q45 65 35 58" stroke="#E8732A" strokeWidth="4" strokeLinecap="round" fill="none"/>
    <path d="M35 58 Q25 65 28 75 Q31 85 42 82" stroke="#E8732A" strokeWidth="4" strokeLinecap="round" fill="none"/>
    <path d="M55 35 Q70 28 75 40 Q80 52 68 58 Q56 64 50 58" stroke="#E8732A" strokeWidth="4" strokeLinecap="round" fill="none"/>
    <path d="M50 58 Q45 68 50 76 Q55 84 65 80" stroke="#E8732A" strokeWidth="4" strokeLinecap="round" fill="none"/>
    <path d="M72 25 Q78 20 80 26 Q82 32 76 34" stroke="#C9A84C" strokeWidth="3" strokeLinecap="round" fill="none"/>
    <circle cx="50" cy="15" r="3" fill="#C9A84C"/>
    <path d="M35 12 Q50 8 65 12" stroke="#C9A84C" strokeWidth="2" strokeLinecap="round" fill="none"/>
  </svg>
);

export default function Navbar() {
  const [scrolled, setScrolled] = useState(false);
  const [menuOpen, setMenuOpen] = useState(false);
  const [active, setActive] = useState('home');

  useEffect(() => {
    const onScroll = () => setScrolled(window.scrollY > 40);
    window.addEventListener('scroll', onScroll);
    return () => window.removeEventListener('scroll', onScroll);
  }, []);

  const links = [
    { id: 'home', label: 'Home' },
    { id: 'about', label: 'About' },
    { id: 'services', label: 'Services' },
    { id: 'principles', label: 'Principles' },
    { id: 'insights', label: 'Insights' },
    { id: 'contact', label: 'Contact' },
  ];

  const scrollTo = (id) => {
    setActive(id);
    setMenuOpen(false);
    document.getElementById(id)?.scrollIntoView({ behavior: 'smooth' });
  };

  return (
    <motion.nav
      initial={{ y: -80 }}
      animate={{ y: 0 }}
      transition={{ duration: 0.6, ease: 'easeOut' }}
      style={{
        position: 'fixed',
        top: 0,
        left: 0,
        right: 0,
        zIndex: 1000,
        padding: '0 5%',
        height: 72,
        display: 'flex',
        alignItems: 'center',
        justifyContent: 'space-between',
        background: scrolled ? 'rgba(13,13,13,0.95)' : 'transparent',
        backdropFilter: scrolled ? 'blur(16px)' : 'none',
        borderBottom: scrolled ? '1px solid rgba(201,168,76,0.1)' : 'none',
        transition: 'all 0.4s ease',
      }}
    >
      {/* Logo */}
      <button
        onClick={() => scrollTo('home')}
        style={{ display: 'flex', alignItems: 'center', gap: 10, background: 'none', border: 'none', cursor: 'pointer' }}
      >
        <OmIcon />
        <div>
          <div style={{ fontFamily: 'Cinzel', fontSize: 18, color: '#F5F0E8', letterSpacing: 2, lineHeight: 1.1 }}>
            VEDIC IT
          </div>
          <div style={{ fontSize: 9, color: '#C9A84C', letterSpacing: 3, textTransform: 'uppercase', lineHeight: 1 }}>
            Clarity Before Code
          </div>
        </div>
      </button>

      {/* Desktop Links */}
      <div style={{ display: 'flex', gap: 32, alignItems: 'center' }} className="desktop-nav">
        {links.map(link => (
          <button
            key={link.id}
            onClick={() => scrollTo(link.id)}
            style={{
              background: 'none',
              border: 'none',
              cursor: 'pointer',
              fontFamily: 'Inter',
              fontSize: 13,
              letterSpacing: 1.5,
              textTransform: 'uppercase',
              color: active === link.id ? '#E8732A' : '#B0A898',
              fontWeight: active === link.id ? 600 : 400,
              transition: 'color 0.3s',
              padding: '4px 0',
              borderBottom: active === link.id ? '1px solid #E8732A' : '1px solid transparent',
            }}
          >
            {link.label}
          </button>
        ))}
        <motion.button
          whileHover={{ scale: 1.04 }}
          whileTap={{ scale: 0.97 }}
          onClick={() => scrollTo('contact')}
          style={{
            background: 'linear-gradient(135deg, #E8732A, #C4581A)',
            border: 'none',
            borderRadius: 4,
            padding: '9px 22px',
            color: 'white',
            fontFamily: 'Inter',
            fontSize: 12,
            letterSpacing: 1.5,
            textTransform: 'uppercase',
            cursor: 'pointer',
            fontWeight: 600,
          }}
        >
          Get in Touch
        </motion.button>
      </div>

      {/* Mobile Hamburger */}
      <button
        onClick={() => setMenuOpen(!menuOpen)}
        style={{ background: 'none', border: 'none', cursor: 'pointer', display: 'none' }}
        className="hamburger"
      >
        <div style={{ width: 24, height: 2, background: '#E8732A', margin: '5px 0', transition: '0.3s' }} />
        <div style={{ width: 16, height: 2, background: '#C9A84C', margin: '5px 0', transition: '0.3s' }} />
        <div style={{ width: 24, height: 2, background: '#E8732A', margin: '5px 0', transition: '0.3s' }} />
      </button>

      {/* Mobile Menu */}
      <AnimatePresence>
        {menuOpen && (
          <motion.div
            initial={{ opacity: 0, y: -20 }}
            animate={{ opacity: 1, y: 0 }}
            exit={{ opacity: 0, y: -20 }}
            style={{
              position: 'absolute',
              top: 72,
              left: 0,
              right: 0,
              background: 'rgba(13,13,13,0.98)',
              backdropFilter: 'blur(20px)',
              borderBottom: '1px solid rgba(201,168,76,0.2)',
              padding: '24px 5%',
              display: 'flex',
              flexDirection: 'column',
              gap: 20,
            }}
          >
            {links.map(link => (
              <button
                key={link.id}
                onClick={() => scrollTo(link.id)}
                style={{
                  background: 'none', border: 'none', cursor: 'pointer',
                  fontFamily: 'Inter', fontSize: 16, letterSpacing: 1.5,
                  textTransform: 'uppercase', color: active === link.id ? '#E8732A' : '#B0A898',
                  textAlign: 'left', fontWeight: active === link.id ? 600 : 400,
                }}
              >
                {link.label}
              </button>
            ))}
          </motion.div>
        )}
      </AnimatePresence>

      <style>{`
        @media (max-width: 768px) {
          .desktop-nav { display: none !important; }
          .hamburger { display: block !important; }
        }
      `}</style>
    </motion.nav>
  );
}
